import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Title, Text, Grid, Card, BarList } from '@tremor/react';
import { KPICard } from '@/components/KPICard';
import { getLotesAdmin, extractErrorMessage } from '@/lib/api';

// ── Tipos (payload de GET /lotes-admin?dias=N — admin-api/routers/lotes.py) ─

interface LoteRow {
  id: number;
  nome: string | null;
  org: string;
  created_at: string;
  nome_customizado: boolean;
  exportado: boolean;
  total: number;
  prontos: number;
  pendencias: number;
  erros: number;
  andamento: number;
  editados: number;
}

interface LotesAdminData {
  disponivel: boolean;
  erro?: string;
  dias: number;
  kpis: {
    lotes: number;
    orgs_distintas: number;
    casos_via_lote: number;
    tamanho_medio: number;
    taxa_entrega_pct: number | null;
    taxa_erro_pct: number | null;
    pendencias_pct: number | null;
    casos_editados_pos_calculo: number;
    editados_pct: number;
    tempo_mediano_min: number | null;
    tempo_p90_min: number | null;
    concluidos_noite_pct: number | null;
    lotes_com_nome_customizado: number;
    lotes_exportados: number;
  };
  motivos_problema: { motivo: string; casos: number }[];
  eventos_uso: Record<string, { eventos: number; lotes_distintos: number }>;
  lotes: LoteRow[];
}

const PERIODOS = [7, 14, 28, 90];

function fmtInt(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—';
  return new Intl.NumberFormat('pt-BR').format(n);
}

function fmtPct(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—';
  return `${n.toFixed(1)}%`;
}

function fmtMin(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—';
  if (n < 90) return `${Math.round(n)} min`;
  return `${(n / 60).toFixed(1)} h`;
}

export function LotesAdminPage() {
  const [dias, setDias] = useState(28);

  const { data, isLoading, error } = useQuery<LotesAdminData>({
    queryKey: ['lotes-admin', dias],
    queryFn: async () => (await getLotesAdmin(dias)).data,
    refetchInterval: 5 * 60_000,
  });

  const errMsg = error
    ? extractErrorMessage(error, 'erro ao carregar lotes')
    : data && !data.disponivel
      ? (data.erro ?? 'dados indisponíveis')
      : null;

  const k = data?.kpis;
  const motivosItems = (data?.motivos_problema ?? []).map((m) => ({
    name: m.motivo,
    value: m.casos,
  }));
  const ev = data?.eventos_uso ?? {};

  return (
    <div className="space-y-6">
      <header className="space-y-1">
        <div className="flex items-center justify-between">
          <Title>Lotes (Cálculos em Massa)</Title>
          <div className="flex items-center gap-1 rounded-md border border-slate-700 p-0.5">
            {PERIODOS.map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => setDias(p)}
                className={`rounded px-2.5 py-1 text-xs transition ${
                  dias === p
                    ? 'bg-blue-600/30 text-blue-200'
                    : 'text-slate-400 hover:bg-slate-800 hover:text-slate-200'
                }`}
              >
                {p}d
              </button>
            ))}
          </div>
        </div>
        <Text>
          Saúde da função de lote: acuracidade (entrega, edição pós-cálculo), erros
          (guards por motivo) e uso (adoção, exports, janela dia/noite).
        </Text>
      </header>

      {!isLoading && errMsg && (
        <Card className="border border-red-800/40 bg-red-900/10">
          <Text className="text-red-300">{errMsg}</Text>
        </Card>
      )}

      {/* Adoção */}
      <Grid numItems={1} numItemsSm={4} className="gap-4">
        <KPICard
          label={`Lotes criados (${dias}d)`}
          value={isLoading ? null : fmtInt(k?.lotes)}
          loading={isLoading}
          error={errMsg}
          hint={`${fmtInt(k?.orgs_distintas)} organização(ões) · ${fmtInt(k?.lotes_com_nome_customizado)} com nome próprio`}
        />
        <KPICard
          label="Casos via lote"
          value={isLoading ? null : fmtInt(k?.casos_via_lote)}
          loading={isLoading}
          error={errMsg}
          hint={`tamanho médio ${k?.tamanho_medio ?? '—'} casos/lote`}
        />
        <KPICard
          label="Taxa de entrega"
          value={isLoading ? null : fmtPct(k?.taxa_entrega_pct)}
          loading={isLoading}
          error={errMsg}
          hint={`erros ${fmtPct(k?.taxa_erro_pct)} · pendências ${fmtPct(k?.pendencias_pct)} (dos terminais)`}
        />
        <KPICard
          label="Lotes exportados"
          value={isLoading ? null : fmtInt(k?.lotes_exportados)}
          loading={isLoading}
          error={errMsg}
          hint={`export = o entregável do escritório · ${ev.lote_export?.eventos ?? 0} downloads`}
        />
      </Grid>

      {/* Acuracidade + vazão */}
      <Grid numItems={1} numItemsSm={4} className="gap-4">
        <KPICard
          label="Edição pós-cálculo"
          value={isLoading ? null : fmtPct(k?.editados_pct)}
          loading={isLoading}
          error={errMsg}
          hint={`${fmtInt(k?.casos_editados_pos_calculo)} caso(s) corrigidos pelo cliente — proxy de acuracidade (quanto menor, melhor)`}
        />
        <KPICard
          label="Tempo mediano/caso"
          value={isLoading ? null : fmtMin(k?.tempo_mediano_min)}
          loading={isLoading}
          error={errMsg}
          hint={`p90 ${fmtMin(k?.tempo_p90_min)} (criação → conclusão)`}
        />
        <KPICard
          label="Concluídos de madrugada"
          value={isLoading ? null : fmtPct(k?.concluidos_noite_pct)}
          loading={isLoading}
          error={errMsg}
          hint="janela D3: lote acelera 20h–8h BRT (avulso tem prioridade de dia)"
        />
        <KPICard
          label="Retries / exclusões"
          value={
            isLoading
              ? null
              : `${ev.lote_retry_falhos?.eventos ?? 0} / ${ev.lote_excluido?.eventos ?? 0}`
          }
          loading={isLoading}
          error={errMsg}
          hint="uso dos botões 'Tentar de novo' e 'Excluir lote'"
        />
      </Grid>

      {/* Motivos de problema */}
      <Grid numItems={1} numItemsSm={2} className="gap-4">
        <Card>
          <Title className="text-base">Motivos de erro/pendência</Title>
          <Text className="text-xs">
            guards agrupados pelo status_message — onde a acuracidade dói
          </Text>
          {motivosItems.length > 0 ? (
            <BarList data={motivosItems} className="mt-4" />
          ) : (
            <Text className="mt-4 text-slate-500">
              {isLoading ? 'carregando…' : 'nenhum problema no período 🎉'}
            </Text>
          )}
        </Card>
        <Card>
          <Title className="text-base">Leitura rápida</Title>
          <ul className="mt-3 space-y-2 text-sm text-slate-300">
            <li>
              • <b>Edição pós-cálculo</b> é o melhor proxy de acuracidade no lote:
              cliente corrigiu = modelo errou (métrica da casa: edições MR/semana).
            </li>
            <li>
              • <b>Pendências</b> (.pjc inválido, zerado retido) têm estorno
              automático e reprocesso — se crescerem, é a dívida .pjc determinístico.
            </li>
            <li>
              • <b>Export baixado</b> é o sinal de valor entregue ao escritório
              (o consolidado vai pro relatório interno dele).
            </li>
          </ul>
        </Card>
      </Grid>

      {/* Tabela por lote */}
      <Card>
        <Title className="text-base">Lotes do período</Title>
        <div className="mt-3 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-800 text-left text-xs uppercase tracking-wide text-slate-400">
                <th className="px-3 py-2">Lote</th>
                <th className="px-3 py-2">Org</th>
                <th className="px-3 py-2">Criado</th>
                <th className="px-3 py-2 text-right">Casos</th>
                <th className="px-3 py-2 text-right">Prontos</th>
                <th className="px-3 py-2 text-right">Pend.</th>
                <th className="px-3 py-2 text-right">Erros</th>
                <th className="px-3 py-2 text-right">Editados</th>
                <th className="px-3 py-2 text-center">Export</th>
              </tr>
            </thead>
            <tbody>
              {(data?.lotes ?? []).map((l) => (
                <tr
                  key={l.id}
                  className="border-b border-slate-900 text-slate-200 hover:bg-slate-800/30"
                >
                  <td className="px-3 py-2">
                    <span className="font-medium">{l.nome || `Lote #${l.id}`}</span>
                    {l.nome_customizado && (
                      <span className="ml-1.5 rounded bg-blue-600/20 px-1 py-px text-[10px] text-blue-300">
                        nome próprio
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-slate-400">{l.org}</td>
                  <td className="px-3 py-2 text-slate-400 tabular-nums">{l.created_at}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{l.total}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-emerald-400">{l.prontos}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-amber-400">
                    {l.pendencias || '—'}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-red-400">
                    {l.erros || '—'}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">{l.editados || '—'}</td>
                  <td className="px-3 py-2 text-center">{l.exportado ? '✅' : '—'}</td>
                </tr>
              ))}
              {!isLoading && (data?.lotes ?? []).length === 0 && (
                <tr>
                  <td colSpan={9} className="px-3 py-6 text-center text-slate-500">
                    nenhum lote no período
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
